import React, { useState } from 'react';
import { Button } from './ui/Button';
import { CheckCircle, AlertCircle } from 'lucide-react';
interface LeadFormProps {
  variant?: 'hero' | 'default';
  className?: string;
}
export function LeadForm({
  variant = 'default',
  className = ''
}: LeadFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    type: '',
    service: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = 'El nombre es obligatorio';
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!formData.phone || formData.phone.length < 9) {
      newErrors.phone = 'Teléfono inválido';
    }
    if (!formData.type) newErrors.type = 'Selecciona un tipo';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        type: '',
        service: ''
      });
    }, 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  if (status === 'success') {
    return <div className={`p-8 rounded-2xl text-center ${variant === 'hero' ? 'bg-white/95 backdrop-blur shadow-xl' : 'bg-white shadow-lg'} ${className}`} data-id="element-215">
        <div className="flex justify-center mb-4" data-id="element-216">
          <CheckCircle className="w-16 h-16 text-green-500" data-id="element-217" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-218">
          ¡Mensaje enviado!
        </h3>
        <p className="text-gray-600 mb-6" data-id="element-219">
          Nos pondremos en contacto contigo en menos de 24 horas.
        </p>
        <Button onClick={() => setStatus('idle')} variant="outline" className="w-full" data-id="element-220">
          Enviar otro mensaje
        </Button>
      </div>;
  }
  const inputClasses = `w-full px-4 py-3 rounded-lg border ${variant === 'hero' ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none`;
  const labelClasses = 'block text-sm font-medium text-gray-700 mb-1';
  return <form onSubmit={handleSubmit} className={`p-6 md:p-8 rounded-2xl ${variant === 'hero' ? 'bg-white/95 backdrop-blur-sm shadow-2xl' : 'bg-white shadow-xl'} ${className}`} data-id="element-221">
      <h3 className="text-xl font-bold text-gray-900 mb-6 text-center" data-id="element-222">
        {variant === 'hero' ? 'Agenda tu llamada gratuita' : 'Solicita tu presupuesto'}
      </h3>

      <div className="space-y-4" data-id="element-223">
        <div data-id="element-224">
          <label htmlFor="name" className={labelClasses} data-id="element-225">
            Nombre completo
          </label>
          <input id="name" name="name" type="text" placeholder="Tu nombre" value={formData.name} onChange={handleChange} className={`${inputClasses} ${errors.name ? 'border-red-500' : ''}`} data-id="element-226" />
          {errors.name && <p className="text-red-500 text-xs mt-1" data-id="element-227">{errors.name}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-228">
          <div data-id="element-229">
            <label htmlFor="email" className={labelClasses} data-id="element-230">
              Email
            </label>
            <input id="email" name="email" type="email" placeholder="tu@email.com" value={formData.email} onChange={handleChange} className={`${inputClasses} ${errors.email ? 'border-red-500' : ''}`} data-id="element-231" />
            {errors.email && <p className="text-red-500 text-xs mt-1" data-id="element-232">{errors.email}</p>}
          </div>
          <div data-id="element-233">
            <label htmlFor="phone" className={labelClasses} data-id="element-234">
              Teléfono
            </label>
            <input id="phone" name="phone" type="tel" placeholder="+34 600 000 000" value={formData.phone} onChange={handleChange} className={`${inputClasses} ${errors.phone ? 'border-red-500' : ''}`} data-id="element-235" />
            {errors.phone && <p className="text-red-500 text-xs mt-1" data-id="element-236">{errors.phone}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-237">
          <div data-id="element-238">
            <label htmlFor="type" className={labelClasses} data-id="element-239">
              Soy...
            </label>
            <select id="type" name="type" value={formData.type} onChange={handleChange} className={`${inputClasses} ${errors.type ? 'border-red-500' : ''}`} data-id="element-240">
              <option value="" data-id="element-241">Seleccionar</option>
              <option value="empresa" data-id="element-242">Empresa</option>
              <option value="administracion" data-id="element-243">Administración</option>
              <option value="particular" data-id="element-244">Particular</option>
            </select>
            {errors.type && <p className="text-red-500 text-xs mt-1" data-id="element-245">{errors.type}</p>}
          </div>
          <div data-id="element-246">
            <label htmlFor="service" className={labelClasses} data-id="element-247">
              Interés
            </label>
            <select id="service" name="service" value={formData.service} onChange={handleChange} className={inputClasses} data-id="element-248">
              <option value="" data-id="element-249">General</option>
              <option value="movilidad" data-id="element-250">Movilidad Internacional</option>
              <option value="fiscalidad" data-id="element-251">Contabilidad y Fiscalidad</option>
              <option value="mercantil" data-id="element-252">Asesoría Mercantil</option>
              <option value="laboral" data-id="element-253">Asesoría Laboral</option>
            </select>
          </div>
        </div>

        <Button type="submit" className="w-full mt-2" size="lg" isLoading={status === 'submitting'} data-id="element-254">
          {variant === 'hero' ? 'Agendar Llamada' : 'Solicitar Presupuesto'}
        </Button>

        <p className="text-xs text-center text-gray-500 mt-4" data-id="element-255">
          Al enviar aceptas nuestra{' '}
          <a href="#" className="underline hover:text-primary" data-id="element-256">
            política de privacidad
          </a>
          .
        </p>
      </div>
    </form>;
}